import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { CellKeyDownEvent, IRowNode } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { ROW_LEVEL } from '../constants';

export interface AddRowPayload<TData = any> {
    node: IRowNode<TData>;
    data: TData;
}

export const useFocusOnNewRow = <TData>(tableRef: MutableRefObject<AgGridReact | null>, tableData?: TData[] | null, focusDelay?: number) => {
    const newRowOnEnterDataRef = useRef<null | { newRowIndex: number; columnId: string }>(null);
    const tableDataSize = tableData?.length || 0;

    // Deliberately saving focus delay in ref to use in main useEffect, as I do not
    // want it to be reactive and re-run the useEffect on change of focusDelay.
    const focusDelayRef = useRef(focusDelay);
    useLayoutEffect(() => {
        focusDelayRef.current = focusDelay;
    }, [focusDelay]);

    useEffect(() => {
        if (newRowOnEnterDataRef.current) {
            const { newRowIndex, columnId } = newRowOnEnterDataRef.current;
            tableRef.current?.api.clearFocusedCell();
            tableRef.current?.api.clearRangeSelection();

            const timeout = setTimeout(() => {
                tableRef.current?.api.ensureIndexVisible(newRowIndex);
                tableRef.current?.api.ensureColumnVisible(columnId);
                tableRef.current?.api.setFocusedCell(newRowIndex, columnId);
            }, focusDelayRef.current);
            newRowOnEnterDataRef.current = null;

            return () => {
                clearTimeout(timeout);
            };
        }

        return undefined;
    }, [tableDataSize, tableRef]);

    const addNewRowInEndOfSection = useCallback(async (event: CellKeyDownEvent<TData>, onAddBelow: (payload: AddRowPayload<TData>, updateTableKey?: boolean) => Promise<boolean>) => {
        const { node, column } = event;
        // Now either this is last non-expanded product row
        // Or last component row of the last expanded product row
        const productNode = node.level === ROW_LEVEL.COMPONENT ? node.parent : node;

        // this won't happen, but TS doesn't know that
        if (!productNode) return;

        // We are always going to add new product row in the section, even
        // when user pressed enter in last component row.
        const data = { ...productNode.data, is_kit: false } as TData;
        const payload: AddRowPayload<TData> = {
            data,
            node: { ...productNode, data }
        };

        const isSuccess = await onAddBelow(payload, true);
        if (isSuccess) {
            // As we are preventing automatic navigation with Enter for last node
            // we need to manually navigate after api success
            const newRowIndex = (node.rowIndex ?? 0) + 1;
            // set the following to be used in useEffect to update focus after render
            newRowOnEnterDataRef.current = { newRowIndex, columnId: column.getColId() };
        }
    }, []);

    return { addNewRowInEndOfSection };
};
